import axios from "axios";
import React, { useEffect, useState } from "react";
import { displayMessage, zeroIfNegative } from "../../Utils/helper";
import { useLocation } from "react-router-dom";
import Loader from "../common/Loader";
import AuthService from "../../services/AuthService";
import { removeSKU, removeDescription } from "../../Utils/skuDescriptionConverter";
import PageHeader from "../common/PageHeader";
import ReportPagination from "../workflow/ReportPagination";

const { REACT_APP_API_BASE_URL } = process.env;
const ITEMS_PER_PAGE = 5;

const ComparisonTable = () => {
  const location = useLocation();
  const { workflow } = location.state || {};

  const searchParams = new URLSearchParams(location.search);
  const workflowIdFromQuery = searchParams.get("workflow_id");
  const workflow_id = workflowIdFromQuery || workflow?.workflow_id;

  const [selectedCity, setSelectedCity] = useState("");
  const [selectedSalesPerson, setSelectedSalesPerson] = useState("");

  const [tableData, setTableData] = useState([]);
  const [cities, setCities] = useState([]);
  const [salesPersons, setSalesPersons] = useState([]);

  const [filteredCityData, setFilteredCityData] = useState([]);
  const [allWeeks, setAllWeeks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoading2, setIsLoading2] = useState(false);
  const [editedData, setEditedData] = useState([]);
  const [isApproved, setIsApproved] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPlans, setSelectedPlans] = useState({});

  useEffect(() => {
    if (!workflow_id) {
      displayMessage("danger", "Workflow ID missing in URL.");
      return;
    }
    getComparisonTable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflow_id]);

  const getComparisonTable = async () => {
    setIsLoading(true);
    try {
      const url = `${REACT_APP_API_BASE_URL}/get_fp?workflow_id=${workflow_id}`;
      const response = await axios.get(url, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: AuthService.getAccessToken(),
          access_token: AuthService.getAccessToken(),
        },
      });

      const data = response.data.data || [];
      setTableData(data);

      // const uniqueCities = [...new Set(data.map((entry) => entry.level5))];
      // setCities(uniqueCities);

      // const allSpNames = [
      //   ...new Set(
      //     data
      //       .flatMap((entry) => entry.skus_data || [])
      //       .map((sku) => sku.sp_name)
      //       .filter((n) => n && n.trim() !== "")
      //   ),
      // ];
      // setSalesPersons(allSpNames);

      const uniqueCities = [...new Set(data.map((entry) => entry.level5))];
      setCities(uniqueCities);

      // 👇 auto select first city
      if (uniqueCities.length > 0) {
        setSelectedCity(uniqueCities[0]);
      }

      const uniqueSalesPersons = Array.from(
        new Map(
          data
            .flatMap((entry) => entry.skus_data || [])
            .filter((sku) => sku.sp_name && sku.sp_name.trim() !== "")
            .map((sku) => [sku.sp_id, { sp_id: sku.sp_id, sp_name: sku.sp_name }])
        ).values()
      );
      setSalesPersons(uniqueSalesPersons);

      const skusData = data.flatMap((entry) => entry.skus_data || []);

      // Default to showing all salespeople for comparison
      setSelectedSalesPerson("");
    } catch (error) {
      console.error("Error fetching workflows:", error.message);
      displayMessage("danger", "Error fetching workflow comparison data");
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (!selectedCity) {
      setFilteredCityData([]);
      setAllWeeks([]);
      setEditedData([]);
      setCurrentPage(1);
      setSelectedPlans({});
      return;
    }

    const cityData = tableData.find((entry) => entry.level5 === selectedCity);
    if (!cityData) {
      setFilteredCityData([]);
      setAllWeeks([]);
      setEditedData([]);
      setCurrentPage(1);
      setSelectedPlans({});
      return;
    }

    const skusForFilter = selectedSalesPerson
      ? cityData.skus_data.filter((s) => String(s.sp_id) === String(selectedSalesPerson))
      : cityData.skus_data;

    const groupedData = Object.values(
      skusForFilter.reduce((acc, skuEntry) => {
        if (!acc[skuEntry.sku]) {
          acc[skuEntry.sku] = { 
            sku: skuEntry.sku, 
            data: [], 
            spPlans: {} 
          };
        }
        let existing = acc[skuEntry.sku].data.find(
          (d) => d.week === skuEntry.week
        );
        if (!existing) {
          acc[skuEntry.sku].data.push({ ...skuEntry });
        } else if (
          (!existing.p_value || existing.p_value === 0) &&
          skuEntry.p_value > 0
        ) {
          existing.p_value = skuEntry.p_value;
        }

        if (skuEntry.sp_id) {
          if (!acc[skuEntry.sku].spPlans[skuEntry.sp_id]) {
            acc[skuEntry.sku].spPlans[skuEntry.sp_id] = {
              sp_name: skuEntry.sp_name,
              weeks: {}
            };
          }
          acc[skuEntry.sku].spPlans[skuEntry.sp_id].weeks[skuEntry.week] = skuEntry.p_value;
        }

        return acc;
      }, {})
    );

    setFilteredCityData(groupedData);
    setCurrentPage(1);

    const isWfApproved = workflow?.approval_status?.toUpperCase() === "APPROVED" || isApproved;
    if (isWfApproved) {
      const autoSelected = {};
      groupedData.forEach(skuGroup => {
        if (skuGroup.spPlans) {
          Object.keys(skuGroup.spPlans).forEach(spId => {
            const spData = skuGroup.spPlans[spId];
            // Check if this spData matches the forecast for this sku
            const hasInput = Object.keys(spData.weeks).length > 0;
            const matches = hasInput && skuGroup.data.every(d => {
              const spVal = spData.weeks[d.week];
              return spVal !== undefined && Number(spVal) === Number(d.forecast);
            });
            if (matches) {
              autoSelected[skuGroup.sku] = String(spId);
            }
          });
        }
      });
      setSelectedPlans(autoSelected);
    } else {
      setSelectedPlans({});
    }

    const weeks = [...new Set(skusForFilter.map((s) => s.week))];
    weeks.sort((a, b) => {
      const [m1, y1] = a.split("-");
      const [m2, y2] = b.split("-");
      return new Date(`${y1}-${m1}-01`) - new Date(`${y2}-${m2}-01`);
    });

    setAllWeeks(weeks);
    setEditedData(
      groupedData.flatMap((g) =>
        g.data.map((e) => ({
          ...e,
          forecast: e.forecast ?? e.f_value ?? "",
        }))
      )
    );
  }, [selectedCity, selectedSalesPerson, tableData]);

  const handleForecastChange = (sku, week, value) => {
    setEditedData((prev) =>
      prev.map((e) =>
        e.sku === sku && e.week === week ? { ...e, forecast: value } : e
      )
    );
    setSelectedPlans((prev) => {
      if (prev[sku]) {
        const newPlans = { ...prev };
        delete newPlans[sku];
        return newPlans;
      }
      return prev;
    });
  };

  const handleUsePlan = (sku, spId) => {
    setSelectedPlans((prev) => ({
      ...prev,
      [sku]: spId
    }));
  };

  const handleUseAll = (spId) => {
    const newSelectedPlans = { ...selectedPlans };
    filteredCityData.forEach((skuGroup) => {
      if (skuGroup.spPlans && skuGroup.spPlans[spId]) {
        const spData = skuGroup.spPlans[spId];
        const hasInput = Object.values(spData.weeks).some(
          (val) => val !== 0 && val !== "0" && val !== "-" && val !== undefined
        );
        if (hasInput) {
          newSelectedPlans[skuGroup.sku] = String(spId);
        }
      }
    });
    setSelectedPlans(newSelectedPlans);
  };

  const approveChanges = async () => {
    if (!workflow_id || !selectedCity) {
      displayMessage("warning", "Select a city and ensure workflow is loaded.");
      return;
    }

    setIsLoading2(true); // 👈 START LOADER

    const uniqueSelectedSpIds = [...new Set(Object.values(selectedPlans))];
    let payloadSpId = selectedSalesPerson;
    if (!payloadSpId && uniqueSelectedSpIds.length === 1) {
      payloadSpId = uniqueSelectedSpIds[0];
    }

    const payload = {
      workflow_id,
      city: selectedCity,
      sp_id: payloadSpId,
      skus_data: editedData.map((e) => {
        const selectedSpId = selectedPlans[e.sku];
        let finalValue = parseFloat(e.forecast);
        
        if (selectedSpId) {
          const citySkuGroup = filteredCityData.find((g) => g.sku === e.sku);
          if (citySkuGroup && citySkuGroup.spPlans && citySkuGroup.spPlans[selectedSpId]) {
            const spWeekValue = citySkuGroup.spPlans[selectedSpId].weeks[e.week];
            if (spWeekValue !== undefined && spWeekValue !== "-") {
              finalValue = parseFloat(spWeekValue);
            }
          }
        }

        return {
          sku: e.sku,
          week: e.week,
          f_value: finalValue,
        };
      }),
    };

    try {
      const url = `${REACT_APP_API_BASE_URL}/update_fcast_values`;

      const res = await axios.post(url, payload, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: AuthService.getAccessToken(),
          access_token: AuthService.getAccessToken(),
        },
      });

      if (res.status === 200) {
        displayMessage("success", "Changes approved successfully!");
        setIsApproved(true);
      } else {
        throw new Error("Approval failed");
      }
    } catch (err) {
      console.error("Error approving:", err.message);
      setIsApproved(false);
      displayMessage("danger", "Approval failed!");
    } finally {
      setIsLoading2(false); // 👈 STOP LOADER
      getComparisonTable();
    }
  };

  const isWorkflowApproved =
    workflow?.approval_status?.toUpperCase() === "APPROVED" || isApproved;

  const totalPages = Math.max(1, Math.ceil(filteredCityData.length / ITEMS_PER_PAGE));
  const indexOfFirstItem = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentSkus = filteredCityData.slice(
    indexOfFirstItem,
    indexOfFirstItem + ITEMS_PER_PAGE
  );

  if (isLoading && tableData.length === 0) {
    return (
      <center
        className="flex flex-col items-center justify-center"
        style={{ height: "60vh" }}
      >
        <Loader size="large" />
      </center>
    );
  }

  return (
    <div className="cmp-page">
      <PageHeader
        title="Comparison Workbench"
        subtitle="Reconciling model forecast with sales input."
        style={{ marginBottom: 12, paddingBottom: 10 }}
      />

      <div className="cmp-filter-bar">
        <div className="cmp-filter-field">
          <span className="cmp-filter-label">Workflow</span>
          <div
            className="cmp-workflow-value"
            title={workflow?.workflow_name || "N/A"}
          >
            {workflow?.workflow_name?.slice(0, 40) || "N/A"}
          </div>
        </div>

        <div className="cmp-filter-field">
          <label htmlFor="city" className="cmp-filter-label">
            Select City
          </label>
          <select
            id="city"
            className="cmp-filter-select"
            value={selectedCity}
            onChange={(e) => setSelectedCity(e.target.value)}
          >
            <option value="">-- Select City --</option>
            {cities.map((city, i) => (
              <option key={i} value={city}>
                {city}
              </option>
            ))}
          </select>
        </div>

        <div className="cmp-filter-field">
          <label htmlFor="approver" className="cmp-filter-label">
            Sales Person
          </label>
          <select
            id="approver"
            className="cmp-filter-select"
            value={selectedSalesPerson}
            onChange={(e) => setSelectedSalesPerson(e.target.value)}
          >
            <option value="">-- All --</option>
            {salesPersons.map((sp, i) => (
              <option key={sp.sp_id ?? i} value={String(sp.sp_id)}>
                {sp.sp_name}
              </option>
            ))}
          </select>
        </div>

        <div className="cmp-filter-action cmp-filter-action--group">
          <span className="cmp-filter-label cmp-filter-label--spacer">
            Action
          </span>
          <div className="cmp-action-row">
            {isLoading2 ? (
              <div className="cmp-approve-loader">
                <Loader size="medium" />
              </div>
            ) : (
              <button
                type="button"
                className="cmp-approve-btn"
                onClick={() => {
                  if (!isWorkflowApproved) approveChanges();
                }}
                style={{
                  opacity: isWorkflowApproved ? 0.55 : 1,
                  cursor: isWorkflowApproved ? "not-allowed" : "pointer",
                }}
              >
                {isWorkflowApproved ? "Approved" : "Approve"}
              </button>
            )}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '8px' }}>
        <span style={{ fontSize: '12px', color: '#666', alignSelf: 'center', fontWeight: '600' }}>Quick Select All For:</span>
        {!selectedSalesPerson ? (() => {
          const spIdsWithInputs = new Set();
          filteredCityData.forEach(skuGroup => {
            if (skuGroup.spPlans) {
              Object.keys(skuGroup.spPlans).forEach(spId => {
                const spData = skuGroup.spPlans[spId];
                const hasInput = Object.values(spData.weeks).some(
                  val => val !== 0 && val !== "0" && val !== "-" && val !== undefined
                );
                if (hasInput) {
                  spIdsWithInputs.add(String(spId));
                }
              });
            }
          });

          return salesPersons
            .filter(sp => spIdsWithInputs.has(String(sp.sp_id)))
            .map(sp => (
              <button
                key={`useall-${sp.sp_id}`}
                onClick={() => handleUseAll(sp.sp_id)}
                style={{
                  background: '#f0f5fa',
                  border: '1px solid #c9d8e4',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '11px',
                  padding: '4px 10px',
                  color: '#0c3c54',
                  fontWeight: '600',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.target.style.background = '#e1edf7'}
                onMouseOut={(e) => e.target.style.background = '#f0f5fa'}
              >
                {sp.sp_name.split(' ')[0]}
              </button>
            ));
        })() : null}
        <button
          onClick={() => setSelectedPlans({})}
          style={{
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '11px',
            padding: '4px 10px',
            color: '#64748b',
            marginLeft: '8px'
          }}
        >
          Clear Selection
        </button>
      </div>

      <div className="cmp-table-wrap">
        <div className="cmp-table-scroll">
          <table className="cmp-data-table">
            <thead>
              <tr>
                <th className="cmp-th cmp-th--left">SKU</th>
                <th className="cmp-th cmp-th--left">Description</th>
                <th className="cmp-th cmp-th--left">Type</th>
                {allWeeks.map((week) => (
                  <th key={week} className="cmp-th cmp-th--center">
                    {week}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {currentSkus.map((skuGroup) => {
                const spIds = skuGroup.spPlans 
                  ? Object.keys(skuGroup.spPlans).filter((spId) => {
                      const spData = skuGroup.spPlans[spId];
                      return Object.values(spData.weeks).some(
                        (val) => val !== 0 && val !== "0" && val !== "-" && val !== undefined
                      );
                    }) 
                  : [];
                const numPlannedRows = Math.max(1, spIds.length);
                const rowSpanCount = 1 + numPlannedRows;

                return (
                  <React.Fragment key={skuGroup.sku}>
                    <tr>
                      <td rowSpan={rowSpanCount} className="cmp-td cmp-td--sku">
                        {removeDescription(skuGroup.sku)}
                      </td>
                      <td rowSpan={rowSpanCount} className="cmp-td cmp-td--desc">
                        {removeSKU(skuGroup.sku)}
                      </td>
                      <td className="cmp-td cmp-td--type">
                        <span className="cmp-type cmp-type--forecasted">
                          Forecasted
                        </span>
                      </td>
                      {allWeeks.map((week) => {
                        const matchedEntry = editedData.find(
                          (entry) =>
                            entry.sku === skuGroup.sku && entry.week === week
                        );
                        const rawForecast =
                          matchedEntry?.forecast ?? matchedEntry?.f_value;
                        const forecastValue =
                          rawForecast === undefined || rawForecast === ""
                            ? "-"
                            : zeroIfNegative(rawForecast);

                        return (
                          <td key={`f-${week}`} className="cmp-td cmp-td--value">
                            <input
                              type="text"
                              className="cmp-input cmp-input--plain"
                              value={forecastValue}
                              onChange={(e) =>
                                handleForecastChange(
                                  skuGroup.sku,
                                  week,
                                  e.target.value
                                )
                              }
                            />
                          </td>
                        );
                      })}
                    </tr>
                    
                    {spIds.length > 0 ? (
                      spIds.map((spId) => {
                        const spData = skuGroup.spPlans[spId];
                        const displayName = selectedSalesPerson 
                          ? "Planned" 
                          : `Planned (${spData.sp_name ? spData.sp_name.split(' ')[0] : 'Unknown'})`;
                        
                        const isSelected = selectedPlans[skuGroup.sku] === String(spId) || selectedPlans[skuGroup.sku] === spId;

                        return (
                          <tr key={`planned-${spId}`} style={{ backgroundColor: isSelected ? '#f4fbfa' : 'transparent' }}>
                            <td className="cmp-td cmp-td--type">
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                                <span className="cmp-type cmp-type--planned" style={{ fontWeight: isSelected ? '600' : 'normal' }} title={spData.sp_name || ""}>
                                  {displayName}
                                </span>
                                {!selectedSalesPerson && (
                                  <button 
                                    title={isSelected ? (isWorkflowApproved ? "Approved Plan" : "Selected") : "Use this plan"}
                                    onClick={() => !isWorkflowApproved && handleUsePlan(skuGroup.sku, spId)}
                                    style={{
                                      background: isSelected ? '#2ca24f' : '#f0f5fa',
                                      border: isSelected ? '1px solid #2ca24f' : '1px solid #c9d8e4',
                                      borderRadius: '4px',
                                      cursor: isWorkflowApproved ? 'default' : 'pointer',
                                      fontSize: '10px',
                                      padding: '2px 6px',
                                      color: isSelected ? '#fff' : '#0c3c54',
                                      fontWeight: '600',
                                      whiteSpace: 'nowrap',
                                      opacity: isWorkflowApproved && !isSelected ? 0.5 : 1
                                    }}
                                  >
                                    {isSelected ? (isWorkflowApproved ? "Approved" : "Selected") : "Use"}
                                  </button>
                                )}
                              </div>
                            </td>
                            {allWeeks.map((week) => {
                              const plannedValue = spData.weeks[week] !== undefined 
                                ? spData.weeks[week] 
                                : "-";
                                
                              const matchedEntry = editedData.find(
                                (entry) =>
                                  entry.sku === skuGroup.sku && entry.week === week
                              );
                              const rawForecast =
                                matchedEntry?.forecast ?? matchedEntry?.f_value;
                              const forecastValue =
                                rawForecast === undefined || rawForecast === ""
                                  ? "-"
                                  : zeroIfNegative(rawForecast);
                                  
                              const isMismatch =
                                plannedValue !== 0 &&
                                forecastValue !== 0 &&
                                plannedValue !== "-" &&
                                forecastValue !== "-" &&
                                Number(plannedValue) !== Number(forecastValue);

                              return (
                                <td key={`p-${spId}-${week}`} className="cmp-td cmp-td--value">
                                  <input
                                    type="text"
                                    disabled
                                    className={`cmp-input${
                                      isMismatch ? " cmp-input--mismatch" : ""
                                    }`}
                                    value={plannedValue}
                                  />
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td className="cmp-td cmp-td--type">
                          <span className="cmp-type cmp-type--planned">Planned</span>
                        </td>
                        {allWeeks.map((week) => {
                          const weekData = skuGroup.data.find(
                            (e) => e.week === week
                          );
                          const matchedEntry = editedData.find(
                            (entry) =>
                              entry.sku === skuGroup.sku && entry.week === week
                          );
                          const rawForecast =
                            matchedEntry?.forecast ?? matchedEntry?.f_value;
                          const forecastValue =
                            rawForecast === undefined || rawForecast === ""
                              ? "-"
                              : zeroIfNegative(rawForecast);
                          const plannedValue =
                            weekData?.p_value === undefined
                              ? "-"
                              : weekData.p_value;
                          const isMismatch =
                            plannedValue !== 0 &&
                            forecastValue !== 0 &&
                            plannedValue !== "-" &&
                            forecastValue !== "-" &&
                            Number(plannedValue) !== Number(forecastValue);

                          return (
                            <td key={`p-fallback-${week}`} className="cmp-td cmp-td--value">
                              <input
                                type="text"
                                disabled
                                className={`cmp-input${
                                  isMismatch ? " cmp-input--mismatch" : ""
                                }`}
                                value={plannedValue}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {filteredCityData.length > 0 && (
        <ReportPagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          infoText={`Page ${currentPage} of ${totalPages} (${filteredCityData.length} SKUs)`}
        />
      )}

      <style>{`
        .cmp-page {
          width: 100%;
          color: #0c3c54;
          display: flex;
          flex-direction: column;
          padding: 4px 4px 12px;
          box-sizing: border-box;
        }
        .cmp-filter-bar {
          display: flex;
          align-items: flex-end;
          flex-wrap: wrap;
          gap: 12px;
          width: 100%;
          background: #fff;
          border: 1px solid #e6eef4;
          border-radius: 10px;
          padding: 10px 12px;
          box-shadow: 0 2px 8px rgba(12, 60, 84, 0.05);
          box-sizing: border-box;
          margin-bottom: 12px;
        }
        .cmp-filter-field {
          display: flex;
          flex-direction: column;
          gap: 4px;
          min-width: 140px;
          flex: 1 1 140px;
        }
        .cmp-filter-action {
          display: flex;
          flex-direction: column;
          gap: 4px;
          flex: 0 0 auto;
          margin-left: auto;
        }
        .cmp-filter-action--group {
          min-width: 0;
        }
        .cmp-action-row {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }
        .cmp-filter-label {
          display: block !important;
          font-size: 12px !important;
          font-weight: 600 !important;
          color: #0c3c54 !important;
          font-family: Poppins, sans-serif !important;
          margin: 0 !important;
          line-height: 1.2 !important;
          white-space: nowrap;
        }
        .cmp-filter-label--spacer {
          visibility: hidden;
        }
        .cmp-workflow-value {
          height: 32px;
          display: flex;
          align-items: center;
          padding: 0 10px;
          border: 1px solid #e6eef4;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 600;
          color: #0c3c54;
          background: #f6fafd;
          font-family: Poppins, sans-serif;
          box-sizing: border-box;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .cmp-filter-select {
          width: 100% !important;
          height: 32px !important;
          padding: 0 10px !important;
          font-size: 12px !important;
          font-weight: 600 !important;
          font-family: Poppins, sans-serif !important;
          color: #0c3c54 !important;
          border: 1px solid #e6eef4 !important;
          border-radius: 6px !important;
          background: #fff !important;
          box-sizing: border-box !important;
          margin: 0 !important;
          line-height: 30px !important;
          outline: none;
        }
        .cmp-filter-select:focus {
          border-color: #032B4E !important;
          box-shadow: 0 0 0 3px rgba(3, 43, 78, 0.08);
        }
        .cmp-approve-btn {
          font-family: Poppins, sans-serif;
          background: #E06639;
          color: #fff;
          border: none;
          border-radius: 6px;
          height: 32px;
          padding: 0 18px;
          font-weight: 600;
          font-size: 12px;
          line-height: 32px;
          box-shadow: 0 3px 10px rgba(224, 102, 57, 0.25);
          box-sizing: border-box;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          white-space: nowrap;
          cursor: pointer;
        }
        .cmp-approve-loader {
          height: 32px;
          min-width: 96px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .cmp-table-wrap {
          width: 100%;
          border: 3px solid #032B4E !important;
          border-radius: 12px !important;
          background: #032B4E !important;
          overflow: hidden !important;
          box-shadow: 0 3px 12px rgba(12, 60, 84, 0.06);
          box-sizing: border-box !important;
        }
        .cmp-table-scroll {
          width: 100%;
          overflow-x: auto;
          overflow-y: auto;
          max-height: 65vh;
          background: #fff;
        }
        .cmp-data-table {
          border-collapse: collapse !important;
          border-spacing: 0 !important;
          width: 100% !important;
          min-width: 100%;
          font-family: Poppins, sans-serif;
          font-size: 12px;
          background: #fff;
        }
        .cmp-th {
          padding: 10px 14px;
          font-size: 12px;
          font-weight: 700;
          color: #fff !important;
          background: #032B4E !important;
          border: 1px solid #032B4E !important;
          white-space: nowrap;
          letter-spacing: 0.02em;
          vertical-align: middle;
        }
        .cmp-th--left { text-align: left; }
        .cmp-th--center { text-align: center; min-width: 88px; }
        .cmp-td {
          border: 1px solid #e2eaf0 !important;
          background: #fff !important;
          vertical-align: middle;
          white-space: nowrap;
          line-height: 1.3;
          font-size: 12px;
          color: #0c3c54;
        }
        .cmp-td--sku {
          padding: 8px 14px;
          font-weight: 600;
        }
        .cmp-td--desc {
          padding: 8px 14px;
          font-weight: 500;
        }
        .cmp-td--type {
          padding: 6px 14px;
          text-align: left;
          min-width: 90px;
        }
        .cmp-td--value {
          padding: 6px 10px;
          text-align: center;
        }
        .cmp-type {
          font-weight: 600;
          font-size: 12px;
          font-family: Poppins, sans-serif;
          line-height: 1.3;
        }
        .cmp-type--forecasted { color: #7a35c8; }
        .cmp-type--planned { color: #2ca24f; }
        .cmp-input {
          width: 64px;
          height: 26px;
          text-align: center;
          border: 1px solid #e6eef4;
          border-radius: 4px;
          font-weight: 600;
          font-size: 12px;
          font-family: Poppins, sans-serif;
          color: #0c3c54;
          background: #fff;
          box-sizing: border-box;
          outline: none;
          padding: 0;
          line-height: 24px;
        }
        .cmp-input--plain {
          border-color: transparent;
          background: transparent;
        }
        .cmp-input--plain:focus {
          border-color: #e6eef4;
          background: #fff;
        }
        .cmp-input--mismatch {
          border-color: #E06639;
          background: #fff7f3;
          color: #E06639;
        }
      `}</style>
    </div>
  );
};

export default ComparisonTable;
